import React, { useCallback, useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import * as PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import S from './group-list.module.scss';
import { AlphabetizeBtn } from '../../../../common/buttons/alphabetize-btn';
import { DancerGridItem } from '../../../../common/dancer-grid-item/dancer-grid-item';
import { LoadingSpinner } from '../../../../common/loading/loading-spinner';

const GroupDancerList = ({
  dancerList,
  sortAscending,
  onSortChange,
  onDancersRemove,
  selectedDancers,
  selectDancers,
  deselectDancers,
  onScrollBottom,
  isLoading }) => {
  const { t } = useTranslation();

  const CLIENT = useApolloClient();

  const listRef = useRef(null);

  // Keep the latest callback/loading flag in refs so maybeFillViewport stays stable
  // (the resize listener subscribes once) while always seeing current values.
  const onScrollBottomRef = useRef(onScrollBottom);
  onScrollBottomRef.current = onScrollBottom;
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;

  // On wide screens a full page of dancers wraps into enough columns that the list
  // is shorter than its fixed-height container, so it never overflows — no scrollbar,
  // and onScroll never fires, making it impossible to load further pages. Request the
  // next page until the list overflows (or onScrollBottom's hasNextPage guard stops us).
  const maybeFillViewport = useCallback(() => {
    const el = listRef.current;
    if (!el || isLoadingRef.current) return;
    if (el.scrollHeight <= el.clientHeight) {
      onScrollBottomRef.current();
    }
  }, []);

  useEffect(() => {
    maybeFillViewport();
  }, [dancerList, isLoading, maybeFillViewport]);

  useEffect(() => {
    window.addEventListener('resize', maybeFillViewport);
    return () => window.removeEventListener('resize', maybeFillViewport);
  }, [maybeFillViewport]);

  const handleToggleDancer = useCallback(
    (dancer) => {
      if(!!selectedDancers.find((id) => id === dancer.id)) {
        deselectDancers([dancer.id]);
      } else {
        selectDancers([dancer.id]);
      }
    }, [selectedDancers, selectDancers, deselectDancers]
  );

  const handleOnScroll = () => {
    if (!listRef.current) return;
    if (listRef.current.scrollHeight - (listRef.current.scrollTop + listRef.current.clientHeight) < 100) {
      onScrollBottom();
    }
  };

  return (
    <div className={S.groupListWrapper}>
      <AlphabetizeBtn handleClick={onSortChange} isAsc={sortAscending} />

      {!dancerList.length && <div className='text-center mt-6'>{t('groupsPage.no_dancers')}</div>}

      <div className={S.groupDancerList} onScroll={handleOnScroll} ref={listRef}>
        {dancerList.map((dancer) => (
          <DancerGridItem
            isScalable
            hasOptions
            key={`GROUP_DANCER_${dancer.id}`}
            dancerId={dancer.id}
            isSelected={!!selectedDancers.find((id) => id === dancer.id)}
            selectDancer={() => handleToggleDancer(dancer)}
            removeDancer={() => {
              deselectDancers([dancer.id]);
              onDancersRemove && onDancersRemove(CLIENT, [dancer.id]);
            }}
          />
        ))}
      </div>
      {isLoading && (
        <div className={S.spinnerWrapper}>
          <LoadingSpinner style={S.spinner} />
        </div>
      )}
    </div>
  );
};

GroupDancerList.displayName = 'GroupDancerList';

GroupDancerList.propTypes = {
  dancerList: PropTypes.arrayOf(PropTypes.object),
  sortAscending: PropTypes.bool,
  onSortChange: PropTypes.func.isRequired,
  onDancersRemove: PropTypes.func,
  selectedDancers: PropTypes.arrayOf(PropTypes.string),
  selectDancers: PropTypes.func.isRequired,
  deselectDancers: PropTypes.func.isRequired,
  onScrollBottom: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

GroupDancerList.defaultProps = {
  dancerList: [],
  sortAscending: true,
  onDancersRemove: undefined,
  selectedDancers: [],
  isLoading: false,
};

export { GroupDancerList };
